import * as React from 'react';
import { Field, FormikProps, FormikValues } from 'formik';
import { FormControl, Grid, MenuItem, TextField, Typography } from '@material-ui/core';
import { injectIntl } from 'react-intl';
import * as models from '@app/models/index';
import classNames from 'classnames';
import { USPSPaymentAccountTypeEnum } from '@app/app.enum';

interface IUSPSShipConfigComponentProps extends models.IIntlProps {
	setExpandAccordion: (value: boolean) => void;
	formikProps: FormikProps<FormikValues>;
}

const USPSShipConfigComponent: React.FunctionComponent<IUSPSShipConfigComponentProps> = ({
	setExpandAccordion,
	formikProps,
	intl,
}) => {
	React.useEffect(() => {
		if (Object.keys(formikProps.errors).length > 0) {
			setExpandAccordion(true);
		}
	}, [formikProps.errors]);

	return (
		<>
			<Grid container={true} className='mt-2'>
				<Grid container={true} item={true} xs={12} className='mb-1' alignItems='center'>
					<Typography noWrap={true} color={'inherit'} variant='body1' className={classNames('display-inline-block')}>
						{intl.formatMessage({
							id: 'app.carrier-account.USPS-Ship-LabelOwner-section-header',
						})}
					</Typography>
				</Grid>
				<Grid item={true} container={true} justifyContent='flex-start' spacing={2}>
					<Grid item={true} xs={12} sm={6}>
						<Field
							name='LabelOwner_CRID'
							variant='outlined'
							label={intl.formatMessage({
								id: 'app.carrier-account.config.USPSLabelOwnerCRID-label',
							})}
							component={TextField}
							type='text'
							value={formikProps.values.LabelOwner_CRID}
							fullWidth={true}
							required={true}
							onChange={(e: React.ChangeEvent) => formikProps.handleChange('LabelOwner_CRID')(e)}
							error={
								!!(
									(formikProps.values.LabelOwner_CRID?.length || formikProps.submitCount) &&
									!!formikProps.errors.LabelOwner_CRID
								)
							}
							helperText={
								formikProps.values.LabelOwner_CRID?.length || formikProps.submitCount
									? formikProps.errors.LabelOwner_CRID
									: null
							}
						/>
					</Grid>
					<Grid item={true} xs={12} sm={6}>
						<Field
							name='LabelOwner_MID'
							variant='outlined'
							label={intl.formatMessage({
								id: 'app.carrier-account.config.USPSMID-label',
							})}
							component={TextField}
							type='text'
							value={formikProps.values.LabelOwner_MID}
							fullWidth={true}
							required={true}
							onChange={(e: React.ChangeEvent) => formikProps.handleChange('LabelOwner_MID')(e)}
							error={
								!!(
									(formikProps.values.LabelOwner_MID?.length || formikProps.submitCount) &&
									!!formikProps.errors.LabelOwner_MID
								)
							}
							helperText={
								formikProps.values.LabelOwner_MID?.length || formikProps.submitCount
									? formikProps.errors.LabelOwner_MID
									: null
							}
						/>
					</Grid>
					<Grid item={true} xs={12} sm={6}>
						<Field
							name='LabelOwner_ManifestMID'
							variant='outlined'
							label={intl.formatMessage({
								id: 'app.carrier-account.config.USPSManifestMID-label',
							})}
							component={TextField}
							type='text'
							value={formikProps.values.LabelOwner_ManifestMID}
							fullWidth={true}
							required={true}
							onChange={(e: React.ChangeEvent) => formikProps.handleChange('LabelOwner_ManifestMID')(e)}
							error={
								!!(
									(formikProps.values.LabelOwner_ManifestMID?.length || formikProps.submitCount) &&
									!!formikProps.errors.LabelOwner_ManifestMID
								)
							}
							helperText={
								formikProps.values.LabelOwner_ManifestMID?.length || formikProps.submitCount
									? formikProps.errors.LabelOwner_ManifestMID
									: null
							}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid container={true} className='mt-2'>
				<Grid container={true} item={true} xs={12} className='mb-1' alignItems='center'>
					<Typography noWrap={true} color={'inherit'} variant='body1' className={classNames('display-inline-block')}>
						{intl.formatMessage({
							id: 'app.carrier-account.USPS-Ship-payment-section-header',
						})}
					</Typography>
				</Grid>
				<Grid item={true} container={true} justifyContent='flex-start' spacing={2}>
					<Grid item={true} xs={12} sm={6}>
						<Field
							name='Payer_CRID'
							variant='outlined'
							label={intl.formatMessage({
								id: 'app.carrier-account.config.USPSPayerCRID-label',
							})}
							component={TextField}
							type='text'
							value={formikProps.values.Payer_CRID}
							fullWidth={true}
							required={true}
							onChange={(e: React.ChangeEvent) => formikProps.handleChange('Payer_CRID')(e)}
							error={
								!!(
									(formikProps.values.Payer_CRID?.length || formikProps.submitCount) &&
									!!formikProps.errors.Payer_CRID
								)
							}
							helperText={
								formikProps.values.Payer_CRID?.length || formikProps.submitCount ? formikProps.errors.Payer_CRID : null
							}
						/>
					</Grid>
					<Grid item={true} xs={12} sm={6}>
						<FormControl fullWidth={true}>
							<TextField
								select={true}
								variant='outlined'
								label={intl.formatMessage({
									id: 'app.carrier-account.config.USPSPaymentAccountType-label',
								})}
								fullWidth={true}
								required={true}
								InputLabelProps={{ shrink: true }}
								SelectProps={{
									renderValue: (value: string) => {
										return intl.formatMessage({
											id: `app.carrier-account.config.USPSPaymentAccountType-${value}-label`,
										});
									},
								}}
								value={formikProps.values.Payer_AccountType}
								onChange={(e) => formikProps.handleChange('Payer_AccountType')(e)}
							>
								{Object.entries(USPSPaymentAccountTypeEnum).map(([_, value]: [string, string], index: number) => (
									<MenuItem key={index} value={value}>
										{intl.formatMessage({
											id: `app.carrier-account.config.USPSPaymentAccountType-${value}-label`,
										})}
									</MenuItem>
								))}
							</TextField>
						</FormControl>
					</Grid>
					<Grid item={true} xs={12} sm={6}>
						<Field
							name='Payer_EPSAccountNumber'
							variant='outlined'
							label={intl.formatMessage({
								id: 'app.carrier-account.config.USPSEPSAccountNumber-label',
							})}
							component={TextField}
							type='text'
							value={formikProps.values.Payer_EPSAccountNumber}
							fullWidth={true}
							onChange={(e: React.ChangeEvent) => formikProps.handleChange('Payer_EPSAccountNumber')(e)}
							error={
								!!(
									(formikProps.values.Payer_EPSAccountNumber?.length || formikProps.submitCount) &&
									!!formikProps.errors.Payer_EPSAccountNumber
								)
							}
							helperText={
								formikProps.values.Payer_EPSAccountNumber?.length || formikProps.submitCount
									? formikProps.errors.Payer_EPSAccountNumber
									: null
							}
						/>
					</Grid>
					<Grid item={true} xs={12} sm={6}>
						<Field
							name='Payer_PermitNumber'
							variant='outlined'
							label={intl.formatMessage({
								id: 'app.carrier-account.config.USPSPermitNumber-label',
							})}
							component={TextField}
							type='text'
							value={formikProps.values.Payer_PermitNumber}
							fullWidth={true}
							onChange={(e: React.ChangeEvent) => formikProps.handleChange('Payer_PermitNumber')(e)}
							error={
								!!(
									(formikProps.values.Payer_PermitNumber?.length || formikProps.submitCount) &&
									!!formikProps.errors.Payer_PermitNumber
								)
							}
							helperText={
								formikProps.values.Payer_PermitNumber?.length || formikProps.submitCount
									? formikProps.errors.Payer_PermitNumber
									: null
							}
						/>
					</Grid>
					<Grid item={true} xs={12} sm={6}>
						<Field
							name='Payer_PermitZIP'
							variant='outlined'
							label={intl.formatMessage({
								id: 'app.carrier-account.config.USPSPermitZIP-label',
							})}
							component={TextField}
							type='text'
							value={formikProps.values.Payer_PermitZIP}
							fullWidth={true}
							onChange={(e: React.ChangeEvent) => formikProps.handleChange('Payer_PermitZIP')(e)}
							error={
								!!(
									(formikProps.values.Payer_PermitZIP?.length || formikProps.submitCount) &&
									!!formikProps.errors.Payer_PermitZIP
								)
							}
							helperText={
								formikProps.values.Payer_PermitZIP?.length || formikProps.submitCount
									? formikProps.errors.Payer_PermitZIP
									: null
							}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid container={true} className='mt-2'>
				<Grid container={true} item={true} xs={12} className='mb-1' alignItems='center'>
					<Typography noWrap={true} color={'inherit'} variant='body1' className={classNames('display-inline-block')}>
						{intl.formatMessage({
							id: 'app.carrier-account.USPS-Ship-ReturnLabelOwner-section-header',
						})}
					</Typography>
				</Grid>
				<Grid item={true} container={true} justifyContent='flex-start' spacing={2}>
					<Grid item={true} xs={12} sm={6}>
						<Field
							name='ReturnLabelOwner_CRID'
							variant='outlined'
							label={intl.formatMessage({
								id: 'app.carrier-account.config.USPSReturnLabelOwnerCRID-label',
							})}
							component={TextField}
							type='text'
							value={formikProps.values.ReturnLabelOwner_CRID}
							fullWidth={true}
							onChange={(e: React.ChangeEvent) => formikProps.handleChange('ReturnLabelOwner_CRID')(e)}
							error={
								!!(
									(formikProps.values.ReturnLabelOwner_CRID?.length || formikProps.submitCount) &&
									!!formikProps.errors.ReturnLabelOwner_CRID
								)
							}
							helperText={
								formikProps.values.ReturnLabelOwner_CRID?.length || formikProps.submitCount
									? formikProps.errors.ReturnLabelOwner_CRID
									: null
							}
						/>
					</Grid>
					<Grid item={true} xs={12} sm={6}>
						<Field
							name='ReturnLabelOwner_MID'
							variant='outlined'
							label={intl.formatMessage({
								id: 'app.carrier-account.config.USPSMID-label',
							})}
							component={TextField}
							type='text'
							value={formikProps.values.ReturnLabelOwner_MID}
							fullWidth={true}
							onChange={(e: React.ChangeEvent) => formikProps.handleChange('ReturnLabelOwner_MID')(e)}
							error={
								!!(
									(formikProps.values.ReturnLabelOwner_MID?.length || formikProps.submitCount) &&
									!!formikProps.errors.ReturnLabelOwner_MID
								)
							}
							helperText={
								formikProps.values.ReturnLabelOwner_MID?.length || formikProps.submitCount
									? formikProps.errors.ReturnLabelOwner_MID
									: null
							}
						/>
					</Grid>
					<Grid item={true} xs={12} sm={6}>
						<Field
							name='ReturnLabelOwner_ManifestMID'
							variant='outlined'
							label={intl.formatMessage({
								id: 'app.carrier-account.config.USPSManifestMID-label',
							})}
							component={TextField}
							type='text'
							value={formikProps.values.ReturnLabelOwner_ManifestMID}
							fullWidth={true}
							onChange={(e: React.ChangeEvent) => formikProps.handleChange('ReturnLabelOwner_ManifestMID')(e)}
							error={
								!!(
									(formikProps.values.ReturnLabelOwner_ManifestMID?.length || formikProps.submitCount) &&
									!!formikProps.errors.ReturnLabelOwner_ManifestMID
								)
							}
							helperText={
								formikProps.values.ReturnLabelOwner_ManifestMID?.length || formikProps.submitCount
									? formikProps.errors.ReturnLabelOwner_ManifestMID
									: null
							}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid container={true} className='mt-2'>
				<Grid container={true} item={true} xs={12} className='mb-1' alignItems='center'>
					<Typography noWrap={true} color={'inherit'} variant='body1' className={classNames('display-inline-block')}>
						{intl.formatMessage({
							id: 'app.carrier-account.USPS-Ship-MailOwner-section-header',
						})}
					</Typography>
				</Grid>
				<Grid item={true} container={true} justifyContent='flex-start' spacing={2}>
					<Grid item={true} xs={12} sm={6}>
						<Field
							name='MailOwner_MID'
							variant='outlined'
							label={intl.formatMessage({
								id: 'app.carrier-account.config.USPSMailOwnerMID-label',
							})}
							component={TextField}
							type='text'
							value={formikProps.values.MailOwner_MID}
							fullWidth={true}
							onChange={(e: React.ChangeEvent) => formikProps.handleChange('MailOwner_MID')(e)}
							error={
								!!(
									(formikProps.values.MailOwner_MID?.length || formikProps.submitCount) &&
									!!formikProps.errors.MailOwner_MID
								)
							}
							helperText={
								formikProps.values.MailOwner_MID?.length || formikProps.submitCount
									? formikProps.errors.MailOwner_MID
									: null
							}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid container={true} className='mt-2'>
				<Grid container={true} item={true} xs={12} className='mb-1' alignItems='center'>
					<Typography noWrap={true} color={'inherit'} variant='body1' className={classNames('display-inline-block')}>
						{intl.formatMessage({
							id: 'app.carrier-account.USPS-Ship-shipper-section-header',
						})}
					</Typography>
				</Grid>
				<Grid item={true} container={true} justifyContent='flex-start' spacing={2}>
					<Grid item={true} xs={12} sm={6}>
						<Field
							name='Shipper_CRID'
							variant='outlined'
							label={intl.formatMessage({
								id: 'app.carrier-account.config.USPSShipperCRID-label',
							})}
							component={TextField}
							type='text'
							value={formikProps.values.Shipper_CRID}
							fullWidth={true}
							onChange={(e: React.ChangeEvent) => formikProps.handleChange('Shipper_CRID')(e)}
							error={
								!!(
									(formikProps.values.Shipper_CRID?.length || formikProps.submitCount) &&
									!!formikProps.errors.Shipper_CRID
								)
							}
							helperText={
								formikProps.values.Shipper_CRID?.length || formikProps.submitCount
									? formikProps.errors.Shipper_CRID
									: null
							}
						/>
					</Grid>
					<Grid item={true} xs={12} sm={6}>
						<Field
							name='Shipper_MID'
							variant='outlined'
							label={intl.formatMessage({
								id: 'app.carrier-account.config.USPSShipperMID-label',
							})}
							component={TextField}
							type='text'
							value={formikProps.values.Shipper_MID}
							fullWidth={true}
							onChange={(e: React.ChangeEvent) => formikProps.handleChange('Shipper_MID')(e)}
							error={
								!!(
									(formikProps.values.Shipper_MID?.length || formikProps.submitCount) &&
									!!formikProps.errors.Shipper_MID
								)
							}
							helperText={
								formikProps.values.Shipper_MID?.length || formikProps.submitCount
									? formikProps.errors.Shipper_MID
									: null
							}
						/>
					</Grid>
				</Grid>
			</Grid>
		</>
	);
};

export default injectIntl(USPSShipConfigComponent);



// WEBPACK FOOTER //
// ./src/components/carriers/usps-ship-config.component.tsx